import { apiService } from './api';
import {
  WikiPage,
  WikiPageHistory,
  CreateWikiPageDto,
  UpdateWikiPageDto,
  WikiPageQueryParams,
} from '../types/wiki';

export class WikiService {
  private readonly basePath = '/api/wiki';

  async getWikiPages(params?: WikiPageQueryParams): Promise<{ data: WikiPage[]; total: number }> {
    // Map FE params to BE filters
    const beParams: any = {};
    if (params?.entityType === 'stakeholder' && params.entityId) beParams.stakeholderId = params.entityId;
    if (params?.entityType === 'organization' && params.entityId) beParams.organizationId = params.entityId;
    if (params?.status) beParams.status = params.status;
    if (params?.page) beParams.page = params.page;
    if (params?.limit) beParams.limit = params.limit;
    if (params?.search) beParams.search = params.search;
    if (params?.sortBy) beParams.sortBy = params.sortBy;
    if (params?.sortOrder) beParams.sortOrder = params.sortOrder;

    const response = await apiService.get(this.basePath, { params: beParams });
    const payload = response.data;
    const mapped = (payload.data || []).map(this.mapFromBackend);
    return { data: mapped, total: payload.total ?? mapped.length };
  }

  async getCounts(): Promise<{ total: number; counts: Record<string, number> }> {
    const response = await apiService.get(`${this.basePath}/counts`);
    return response.data;
  }

  async getWikiPage(id: string): Promise<WikiPage> {
    const response = await apiService.get(`${this.basePath}/${id}`);
    return this.mapFromBackend(response.data);
  }

  async getWikiPagesByEntity(entityType: 'stakeholder' | 'organization', entityId: string): Promise<WikiPage[]> {
    const params = entityType === 'stakeholder' ? { stakeholderId: entityId } : { organizationId: entityId };
    const response = await apiService.get(this.basePath, { params });
    const payload = response.data;
    return (payload.data || payload || []).map(this.mapFromBackend);
  }

  async createWikiPage(data: CreateWikiPageDto): Promise<WikiPage> {
    // Map FE shape to BE shape
    const bePayload: any = { title: data.title, content: data.content };
    if (data.entityType === 'stakeholder') bePayload.stakeholderId = data.entityId;
    if (data.entityType === 'organization') bePayload.organizationId = data.entityId;

    const response = await apiService.post(this.basePath, bePayload);
    const body = response.data;
    // If pending entry created for regular user, just reflect a draftish placeholder
    if (body && body.entityData) {
      return this.mapFromBackend({ ...bePayload, id: body.id, status: 'pending', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), createdBy: '' });
    }
    return this.mapFromBackend(body);
  }

  async updateWikiPage(id: string, data: UpdateWikiPageDto): Promise<WikiPage> {
    // Backend expects PATCH for updates
    const response = await apiService.patch(`${this.basePath}/${id}`, data);
    const body = response.data;
    return this.mapFromBackend(body.id ? body : { id, ...body });
  }

  async deleteWikiPage(id: string): Promise<void> {
    await apiService.delete(`${this.basePath}/${id}`);
  }

  async getWikiPageHistory(id: string): Promise<WikiPageHistory[]> {
    const response = await apiService.get(`${this.basePath}/${id}/history`);
    const list = response.data as any[];
    return (Array.isArray(list) ? list : [list]).map((w, idx) => ({
      id: w.id || `${id}-v${idx + 1}`,
      wikiPageId: w.id || id,
      title: w.title,
      content: w.content,
      version: idx + 1,
      createdBy: w.createdBy,
      createdAt: w.createdAt,
      changeDescription: w.changeDescription || '',
    }));
  }

  async getWikiPageVersion(id: string, version: number): Promise<WikiPageHistory> {
    const response = await apiService.get(`${this.basePath}/${id}/history/${version}`);
    return response.data;
  }

  async restoreWikiPageVersion(id: string, version: number): Promise<WikiPage> {
    const response = await apiService.post(`${this.basePath}/${id}/restore/${version}`);
    return this.mapFromBackend(response.data);
  }

  async toggleLock(id: string, isLocked: boolean): Promise<WikiPage> {
    const response = await apiService.patch(`${this.basePath}/${id}`, { isLocked });
    const body = response.data;
    return this.mapFromBackend(body.id ? body : { id, ...body });
  }

  private mapFromBackend = (w: any): WikiPage => {
    // Map BE WikiPage to FE WikiPage shape
    const entityType = w.stakeholderId ? 'stakeholder' : 'organization';
    const entityId = w.stakeholderId || w.organizationId;
    // Derive entity name from loaded relations
    const entityName = w.stakeholder
      ? `${w.stakeholder.firstName || ''} ${w.stakeholder.lastName || ''}`.trim()
      : w.organization?.name || '';
    return {
      id: w.id,
      title: w.title,
      content: w.content,
      entityType,
      entityId,
      entityName,
      version: w.version || 1,
      status: w.status || 'draft',
      isLocked: w.isLocked || false,
      createdBy: w.createdBy,
      creator: w.creator,
      createdAt: w.createdAt,
      updatedAt: w.updatedAt,
      approvedBy: w.approvedBy,
      approver: w.approver,
      approvedAt: w.approvedAt,
    };
  };
}

export const wikiService = new WikiService();
